import React, { useState } from 'react';
import { Link, Route, Switch, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Button,
  Form,
  Header,
  Icon,
  Message,
  Segment,
  Grid,
} from 'semantic-ui-react';
import {
  PRIVACY_POLICY_PAGE_PATH,
  WITHDRAWAL_TYPE_OPTIONS,
} from '../legalPages';
import { getAuthToken, getClientAud } from '../selectors';

const panelStyle = {
  background: '#fff',
  color: '#1a1a1a',
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.1)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 8px 28px rgba(0, 0, 0, 0.06)',
  padding: '1.75rem 2rem 2rem',
  maxWidth: 720,
  margin: '0 auto',
};

function WiderrufIntro() {
  const history = useHistory();

  return (
    <div className="container widerruf-page" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={panelStyle}>
        <Header as="h2" style={{ fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
          Vertrag widerrufen
        </Header>
        <p style={{ color: '#888', fontSize: 13, marginBottom: '1rem' }}>
          Schritt 1 von 2
        </p>
        <p style={{ color: '#444', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Du kannst deinen Kauf eines Kuscheldate-Abonnements widerrufen. Der Widerruf ist nur wirksam,
          wenn ein gesetzliches oder vertragliches Widerrufsrecht besteht und die Widerrufsfrist noch
          nicht abgelaufen ist.
        </p>
        <Message info style={{ borderRadius: 8 }}>
          <Icon name="info circle" />
          <Message.Content>
            <p style={{ margin: 0, lineHeight: 1.55 }}>
              Ein Widerruf ist nur wirksam, wenn ein gesetzliches oder vertragliches Widerrufsrecht
              besteht und die Widerrufsfrist noch nicht abgelaufen ist.
            </p>
          </Message.Content>
        </Message>
        <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
          <Button
            primary
            size="large"
            className="cuddle"
            onClick={() => history.push('/widerruf/form')}
            data-cy="widerruf-start"
          >
            Vertrag widerrufen
          </Button>
        </div>
      </div>
    </div>
  );
}

function WiderrufForm() {
  const history = useHistory();
  const authToken = useSelector(getAuthToken);
  const clientAud = useSelector(getClientAud);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');
  const [withdrawalType, setWithdrawalType] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);

  async function onSubmit(ev) {
    ev.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/v1/withdrawals', {
        method: 'POST',
        // The page is public, so send the token only when there is one. Without
        // it the server cannot tell who filed the Widerruf, and an admin is left
        // guessing from the email address the member typed by hand.
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}`, JWT_AUD: clientAud } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          order_id: orderId.trim(),
          withdrawal_type: withdrawalType,
          note: note.trim() || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = json?.error?.detail;
        const msg = typeof detail === 'object'
          ? Object.values(detail).flat().join(' ')
          : (detail || 'Der Widerruf konnte nicht gesendet werden.');
        setError(msg);
        return;
      }
      setSubmittedAt(json.submitted_at || new Date().toISOString());
    } catch (_) {
      setError('Der Widerruf konnte nicht gesendet werden. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
    }
  }

  if (submittedAt) {
    return (
      <div className="container widerruf-page" style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={panelStyle}>
          <Header as="h2" style={{ fontWeight: 600, color: '#1a1a1a' }}>
            Widerruf eingegangen
          </Header>
          <Message success style={{ borderRadius: 8 }}>
            <Message.Content>
              <p style={{ margin: 0, lineHeight: 1.55 }}>
                Dein Widerruf wurde übermittelt. Du erhältst in Kürze eine Eingangsbestätigung
                mit Inhalt, Datum und Uhrzeit an
                {' '}
                <strong>{email}</strong>
                .
              </p>
            </Message.Content>
          </Message>
          <Button basic onClick={() => history.push('/')}>
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container widerruf-page" style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={panelStyle}>
        <Header as="h2" style={{ fontWeight: 600, marginBottom: 8, color: '#1a1a1a' }}>
          Widerrufsformular
        </Header>
        <p style={{ color: '#888', fontSize: 13, marginBottom: '1rem' }}>
          Schritt 2 von 2
        </p>

        <Message info style={{ borderRadius: 8, marginBottom: '1rem' }}>
          <Message.Content>
            <p style={{ margin: 0, lineHeight: 1.55 }}>
              Ein Widerruf ist nur wirksam, wenn ein gesetzliches oder vertragliches Widerrufsrecht
              besteht und die Widerrufsfrist noch nicht abgelaufen ist.
            </p>
          </Message.Content>
        </Message>

        <Message warning icon style={{ borderRadius: 8, marginBottom: '1.25rem' }}>
          <Message.Content>
            <p style={{ margin: 0, lineHeight: 1.55 }}>
              Die Bestell-/Abo-Referenz muss exakt übereinstimmen. Bei Nichtzuordnung kann der
              Widerruf nicht bearbeitet werden. Die Referenz findest du in deiner
              Bestätigungs-E-Mail nach der Buchung.
            </p>
          </Message.Content>
        </Message>

        {error && (
          <Message error onDismiss={() => setError(null)} content={error} style={{ marginBottom: '1rem' }} />
        )}

        <Form onSubmit={onSubmit}>
          <Grid stackable columns={2}>
            <Grid.Column>
              <Form.Input
                required
                label="Vorname"
                value={firstName}
                onChange={(_, d) => setFirstName(d.value)}
                data-cy="widerruf-first-name"
              />
            </Grid.Column>
            <Grid.Column>
              <Form.Input
                required
                label="Nachname"
                value={lastName}
                onChange={(_, d) => setLastName(d.value)}
                data-cy="widerruf-last-name"
              />
            </Grid.Column>
          </Grid>

          <Form.Input
            required
            type="email"
            label="E-Mail"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            data-cy="widerruf-email"
          />

          <Form.Input
            required
            label="Bestell-/Abo-Referenz"
            value={orderId}
            onChange={(_, d) => setOrderId(d.value)}
            data-cy="widerruf-order-id"
          />

          <Message info size="mini" style={{ marginBottom: '1rem' }}>
            Alternativ zur Bestell-/Abo-Referenz kannst du hier auch deine Rechnungsnummer angeben.
          </Message>

          <Form.Select
            required
            label="Art des Widerrufsrechts"
            options={WITHDRAWAL_TYPE_OPTIONS}
            value={withdrawalType}
            onChange={(_, d) => setWithdrawalType(d.value)}
            data-cy="widerruf-type"
          />

          <Form.TextArea
            label="Anmerkung (optional)"
            value={note}
            onChange={(_, d) => setNote(d.value)}
            rows={4}
            data-cy="widerruf-note"
          />

          <p style={{ fontSize: 14, color: '#555', marginBottom: '1.25rem', lineHeight: 1.5 }}>
            Deine Daten werden ausschließlich zur Bearbeitung deines Widerrufs verwendet.{' '}
            <Link to={PRIVACY_POLICY_PAGE_PATH} target="_blank" rel="noopener noreferrer">
              Datenschutzerklärung
            </Link>
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between' }}>
            <Button
              basic
              type="button"
              icon="arrow left"
              content="Zurück"
              onClick={() => history.push('/widerruf')}
            />
            <Button
              primary
              className="cuddle"
              type="submit"
              loading={loading}
              disabled={!withdrawalType}
              content="Widerruf jetzt bestätigen"
              data-cy="widerruf-submit"
            />
          </div>
        </Form>
      </div>
    </div>
  );
}

export default function WiderrufPage() {
  return (
    <Switch>
      <Route exact path="/widerruf/form" component={WiderrufForm} />
      <Route exact path="/widerruf" component={WiderrufIntro} />
    </Switch>
  );
}

export { WiderrufIntro, WiderrufForm };
